import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { partnersReportApi, type PartnerReportRow } from '../../api/partnersReport.js';
import { ErrorBox, Field } from '../../components/ui.js';
import { localDate, num, money } from '../../lib/format.js';

/**
 * 도메인 모니터링 — 원본 partner.bananamall.co.kr/util/partners_report.php.
 *
 * 하루와 파트너 아이디 목록을 주면 파트너마다 네 가지를 센다.
 *   유입   CM_Click_NowData_<YYYYMM>.click 합계 (regdate 가 'YYMMDD…' 문자열이다)
 *   주문   CM_Price_NowData_<YYYYMM> 의 그 날 건수
 *   가입   CM_Join_Data 의 그 날 가입 수 (이 표만 월 분할이 없다)
 *   금액   같은 주문의 payment 합계
 *
 * 원본과 같게 둔 것
 *  - 네 지표와 그 정의, 하루 단위 조회, 기본 일자는 오늘.
 *  - 맨 아래 **엑셀 붙여넣기용 두 줄**(아이디 줄 / 숫자 줄, 탭 구분). 원본이
 *    이 화면을 쓰던 이유가 그 두 줄이라, 순서(유입·주문·가입·금액)까지 그대로다.
 *
 * 원본과 다르게 한 것
 *  - 원본은 이 관리자와 별개로 **비밀번호 한 줄('woo8854')이 소스에 박혀 있는**
 *    자기만의 문이 있었다. 옮기면서 없애고, 메뉴의 '마케팅' 섹션 권한을 따른다.
 *  - 원본은 붙여넣은 글을 탭으로만 잘라서 **줄바꿈으로 붙여넣으면 아이디 하나로
 *    뭉쳐 0 만 나왔다.** 여기서는 탭·줄바꿈·쉼표를 모두 구분자로 본다.
 *  - 결과를 표로도 보여준다(원본은 두 줄짜리 textarea 뿐이었다). 같은 아이디를
 *    두 번 적으면 한 번만 센다.
 */

function excelText(rows: PartnerReportRow[]): string {
  // 원본 :102-108 - 아이디 한 칸에 숫자 네 칸이 붙으므로 아이디 줄은 탭 4개로 띄운다.
  const ids = rows.map((r) => `${r.partnerId}\t\t\t\t`).join('');
  const nums = rows.map((r) => `${r.click}\t${r.order}\t${r.join}\t${r.pay}\t`).join('');
  return `${ids}\n${nums}`;
}

export function PartnersReportPage() {
  const [date, setDate] = useState(localDate());
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);

  const run = useMutation({
    mutationFn: () => partnersReportApi.run(date, text),
    onSuccess: () => setCopied(false),
  });

  const rows = run.data?.rows ?? [];
  const output = useMemo(() => excelText(rows), [rows]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (sum, row) => ({
          click: sum.click + row.click,
          order: sum.order + row.order,
          join: sum.join + row.join,
          pay: sum.pay + row.pay,
        }),
        { click: 0, order: 0, join: 0, pay: 0 },
      ),
    [rows],
  );

  async function copyOutput() {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
    } catch {
      // 클립보드를 막아 둔 브라우저도 있다. 아래 textarea 를 직접 긁으면 된다.
      setCopied(false);
    }
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">도메인 모니터링</h1>
          <p className="page-desc">
            파트너 아이디를 붙여넣고 일자를 고르면 그 날의 유입·주문·가입·주문금액을 파트너별로 셉니다.
            엑셀에서 아이디 칸을 그대로 긁어 붙여넣어도 됩니다 (탭·줄바꿈·쉼표 모두 구분자로 봅니다).
          </p>
        </div>
      </header>

      <form
        className="lookup-form"
        onSubmit={(event) => {
          event.preventDefault();
          run.mutate();
        }}
      >
        <div className="lookup-range">
          <Field label="모니터링 일자">
            <input
              className="input"
              type="date"
              aria-label="모니터링 일자"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </Field>
        </div>

        <Field label="파트너 아이디들">
          <textarea
            className="input lookup-input"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={'엑셀에서 파트너아이디만 긁어서 붙여넣기\npartner1\tpartner2\tpartner3'}
            rows={8}
            spellCheck={false}
          />
        </Field>

        <div className="lookup-actions">
          <span className="lookup-count">
            {text.trim() === '' ? '파트너 아이디를 붙여넣어 주세요' : '아이디마다 네 가지를 셉니다'}
          </span>
          <button className="btn" type="submit" disabled={run.isPending || text.trim() === ''}>
            {run.isPending ? '세는 중…' : '체크하기'}
          </button>
        </div>
      </form>

      {run.isError && <ErrorBox error={run.error} />}

      {run.data && (
        <>
          <div className="summary">
            <div className="summary__item">
              <span className="summary__label">파트너</span>
              <strong className="summary__value">{num(rows.length)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">유입</span>
              <strong className="summary__value">{num(totals.click)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">주문</span>
              <strong className="summary__value">{num(totals.order)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">가입</span>
              <strong className="summary__value">{num(totals.join)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">주문금액</span>
              <strong className="summary__value">{money(totals.pay)}</strong>
            </div>
          </div>

          {run.data.duplicates > 0 && (
            <p className="lookup-count">
              같은 아이디를 {num(run.data.duplicates)}번 더 적어서 한 번씩만 셌습니다.
            </p>
          )}

          {rows.length === 0 ? (
            <div className="panel">읽어낸 파트너 아이디가 없습니다.</div>
          ) : (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>파트너</th>
                      <th className="td-right">유입</th>
                      <th className="td-right">주문</th>
                      <th className="td-right">가입</th>
                      <th className="td-right">주문금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.partnerId}>
                        <td className="token-cell">{row.partnerId}</td>
                        <td className="td-right">{num(row.click)}</td>
                        <td className="td-right">{num(row.order)}</td>
                        <td className="td-right">{num(row.join)}</td>
                        <td className="td-right">{money(row.pay)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="row--total">
                      <td>합계</td>
                      <td className="td-right">{num(totals.click)}</td>
                      <td className="td-right">{num(totals.order)}</td>
                      <td className="td-right">{num(totals.join)}</td>
                      <td className="td-right">{money(totals.pay)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="panel">
                <h2 className="panel__title">엑셀 붙여넣기용 (원본과 같은 두 줄)</h2>
                <textarea
                  className="input"
                  aria-label="엑셀 붙여넣기용"
                  readOnly
                  rows={4}
                  value={output}
                  onFocus={(event) => event.currentTarget.select()}
                />
                <div className="lookup-actions">
                  <span className="lookup-count">
                    {copied ? '복사했습니다. 엑셀에 붙여넣으세요.' : '전체 선택 → 복사 후 엑셀에 붙여넣기'}
                  </span>
                  <button className="btn" type="button" onClick={() => void copyOutput()}>
                    복사
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
